import { useState } from 'react';
import { collection, addDoc } from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';
import TextInput from '../forms/formInput';
import Label from '../forms/formLabel';
import FormGroup from '../forms/formGroup';
import SubmitButton from '../forms/formSubmitButton';
import FormError from '../forms/formError';
import FormDropdown from '../forms/formDropdown';
import { validateLength, validateEmail } from '../../utils';
import Loader from '../global/loader';
import { firestore } from '../../libs/firebase';
import usStates from '../../utils/states.json';
import { fileMimeTypes, fileExtensions } from '../../utils';
import { uploadFile } from '../../utils/uploader';
import Fetcher from '../../utils/fetcher';
import { getConfig } from '../../config';

const fetcher = new Fetcher();

let formData = {};
const wholesaleRequestsCollection = collection(firestore, getConfig('WHOLESALE_COLLECTION'));
const StateOptions = Object.keys(usStates).map((stateCode) => ({
  label: usStates[stateCode],
  value: stateCode,
}));

const validateFieldsConfig = {
  firstName: [validateLength],
  lastName: [validateLength],
  email: [validateEmail],
  legalBusinessName: [validateLength],
  taxId: [validateLength],
  address: [validateLength],
  city: [validateLength],
  state: [validateLength],
  zipcode: [(value, field) => validateLength(value, field, 3, 15)],
};

const validateForm = (data, handleErrors) => {
  let errors = {};

  try {
    for (const [field, validators] of Object.entries(validateFieldsConfig)) {
      const validatorErrors = [];

      for (const validator of validators) {
        try {
          validator(data[field], field);
        } catch (validatorError) {
          console.log(validatorError);
          validatorErrors.push(validatorError.message);
        }
      }

      if (validatorErrors.length) errors[field] = validatorErrors;
    }

    if (Object.keys(errors).length) throw new Error('Form Validation failed');
  } catch (e) {
    if (handleErrors) handleErrors(errors);

    throw new Error(e.message);
  }
};

const Home = () => {
  const [formErrors, setFormErrors] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState();
  const [success, setSuccess] = useState();

  const handleInputChange = (e) => {
    if (e.target.name === 'state') {
      const val = JSON.parse(e.target.value);
      formData[e.target.name] = val.value;
      formData['stateName'] = val.label;

      return;
    }

    formData[e.target.name] = e.target.value;
  };

  const handleUploadFile = async (files) => {
    try {
      if (!files.length)
        throw new Error('Must upload a Certificate to continue');

      let result;

      for (let index = 0; index < 1; index++) {
        try {
          const file = files?.item(index);
          const generatedFileName = uuidv4();
          if (!fileMimeTypes.includes(file?.type)) {
            const validExtensions = fileExtensions.join(', ');
            throw new Error(
              `Invalid file type. File extension must be one of: ${validExtensions}`
            );
          }

          const { uploadResult } = await uploadFile(
            file,
            `${generatedFileName}.${file.name.substring(
              file.name.lastIndexOf('.') + 1,
              file.name.length
            )}`
          );

          const publicUrl = await fetcher.post({
            path: '/files/getFileLink',
            body: { fileName: uploadResult?.[0]?.name },
          });

          result = publicUrl.data.url;
        } catch (err) {
          throw err;
        }
      }

      return result;
    } catch (e) {
      throw new Error(e);
    }
  };

  const handleError = (errorMessage) => {
    setError(errorMessage);

    setTimeout(() => setError(), 4000);
  };

  const handleSuccess = () => {
    setSuccess(1);

    setTimeout(() => setSuccess(), 4000);
  };

  const handleSubmit = async (e) => {
    try {
      setIsLoading(true);
      
      e.preventDefault();
      
      validateForm(formData, setFormErrors);

      formData.file = await handleUploadFile(
        document.getElementById('certificate').files
      );

      formData.status = 'shopify';
      formData.createdAt = new Date();
      formData.updatedAt = new Date();
      formData.createdAtEpoch = Date.now();

      await addDoc(wholesaleRequestsCollection, formData);
      setFormErrors();
      handleSuccess();
    } catch (error) {
      handleError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container px-4 mt-8 pb-9 mx-auto shadow-lg rounded-xl flex-col flex">
      <img
        width={300}
        className="self-center"
        // src="https://res.cloudinary.com/monday-platform/image/upload/v1648047382/board_views_images/logos/1648047381497_5467c68f-3435-9759-73d2-842707182081.png"
        src="https://cdn.shopify.com/s/files/1/2310/3803/files/MNC_Royal_Pups_Logo_360x.png?v=1651768416"
        alt="Mari Nicole - Wholesale Customer"
      />
      <h1 className="text-3xl font-semibold text-center pb-5">
        Marie Nicole Wholesale Customer Request
      </h1>
      <p className="text-center">
        Apply to Marie Nicole Wholesale Customer Program.
      </p>
      <hr className="my-9" />
      <form
        onSubmit={handleSubmit}
        className="w-full lg:w-3/5 px-3 mx-auto py-8 pt-0"
      >
        <FormGroup>
          <p className="text-sm text-red-400 text-right">
            (*) - Required field
          </p>
        </FormGroup>
        <FormGroup>
          <Label htmlFor="firstName">
            First Name<sup className="text-red-400">*</sup>:
          </Label>
          <TextInput
            onChange={handleInputChange}
            name="firstName"
            id="firstName"
            required={true}
          />
          <FormError errors={formErrors} errorKey="firstName" />
        </FormGroup>
        <FormGroup>
          <Label htmlFor="lastName">
            Last Name<sup className="text-red-400">*</sup>:
          </Label>
          <TextInput
            onChange={handleInputChange}
            name="lastName"
            id="lastName"
            required={true}
          />
          <FormError errors={formErrors} errorKey="lastName" />
        </FormGroup>
        <FormGroup>
          <Label htmlFor="email">
            Email<sup className="text-red-400">*</sup>:
          </Label>
          <TextInput
            onChange={handleInputChange}
            name="email"
            id="email"
            required={true}
          />
          <FormError errors={formErrors} errorKey="email" />
        </FormGroup>
        <FormGroup>
          <Label htmlFor="legalBusinessName">
            Legal Business Name<sup className="text-red-400">*</sup>:
          </Label>
          <TextInput
            onChange={handleInputChange}
            name="legalBusinessName"
            id="legalBusinessName"
            required={true}
          />
          <FormError errors={formErrors} errorKey="legalBusinessName" />
        </FormGroup>
        <FormGroup>
          <Label htmlFor="taxId">
            Tax ID<sup className="text-red-400">*</sup>:
          </Label>
          <TextInput
            onChange={handleInputChange}
            name="taxId"
            id="taxId"
            required={true}
          />
          <FormError errors={formErrors} errorKey="taxId" />
        </FormGroup>
        <FormGroup>
          <Label htmlFor="certificate">
            Resale Certificate<sup className="text-red-400">*</sup>:
          </Label>
          <input type="file" name="certificate" id="certificate" />
          <p className='mt-2 text-red-400'><b>Note: Only image formats (.jpg, .png) and PDFs are allowed.</b></p>
        </FormGroup>
        {/* Addresss */}
        <FormGroup>
          <Label htmlFor="address">
            Address<sup className="text-red-400">*</sup>:
          </Label>
          <TextInput
            onChange={handleInputChange}
            name="address"
            id="address"
            required={true}
          />
          <FormError errors={formErrors} errorKey="address" />
        </FormGroup>
        <FormGroup>
          <Label htmlFor="city">
            City<sup className="text-red-400">*</sup>:
          </Label>
          <TextInput
            onChange={handleInputChange}
            name="city"
            id="city"
            required={true}
          />
          <FormError errors={formErrors} errorKey="city" />
        </FormGroup>
        <FormGroup>
          <Label htmlFor="state">
            State<sup className="text-red-400">*</sup>:
          </Label>
          <FormDropdown
            onChange={handleInputChange}
            name="state"
            id="state"
            required={true}
            options={StateOptions}
          />
          <FormError errors={formErrors} errorKey="state" />
        </FormGroup>
        <FormGroup>
          <Label htmlFor="zipcode">
            zipcode<sup className="text-red-400">*</sup>:
          </Label>
          <TextInput
            onChange={handleInputChange}
            name="zipcode"
            id="zipcode"
            required={true}
          />
          <FormError errors={formErrors} errorKey="zipcode" />
        </FormGroup>
        <hr className="my-9" />
        {error && <p className='bg-red-500 p-2 my-3 rounded text-white text-center font-bold'>An error ocurred. Please contact us for more information.</p>}
        {success && <p className='bg-green-500 p-2 my-3 rounded text-white text-center font-bold'>Thank you! Our staff will review your application and contact you soon.</p>}
        <SubmitButton disabled={isLoading}>
          {isLoading ? <Loader /> : 'APPLY FOR WHOLESALE'}
        </SubmitButton>
      </form>
    </div>
  );
};

export default Home;
